import { useState } from "react";
import { ArrowUpRight, MapPin, Zap, Leaf, TrendingUp } from "lucide-react";
import { CornerMarks, StatusLED } from "@/components/infra";
import VentureApplyModal from "@/sections/VentureApplyModal";
import DeckAccessModal from "@/sections/DeckAccessModal";
import { DECK_LIBRARY } from "@/lib/decks";
import { InlineChatLink } from "@/components/InlineChatLink";

const VENTURES_DECK = DECK_LIBRARY.find((deck) => deck.key === "ventures");

const STATS = [
  { icon: Zap, v: "100–120 MW", k: "Planned hyperscale capacity" },
  { icon: MapPin, v: "Guanacaste, CR", k: "Campus location" },
  { icon: Leaf, v: "Renewable-led", k: "Costa Rica grid profile" },
  { icon: TrendingUp, v: "$3T", k: "Projected AI buildout by 2030" },
];

const STEPS = [
  { n: "01", t: "Apply", d: "DCYB members move to the front of the queue." },
  { n: "02", t: "Review", d: "Every application lands on the founder's desk." },
  { n: "03", t: "Build", d: "Operator playbooks, brand support, weekly cadence." },
  { n: "04", t: "Launch", d: "DCYB channels, warm intros, press, and hiring." },
];

export default function CaseStudy() {
  const [applyOpen, setApplyOpen] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);
  return (
    <section
      id="case-study"
      data-testid="case-study-section"
      data-chat-safe-area
      className="border-b hairline bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-12 gap-y-6 gap-x-6 mb-12 md:mb-16">
          <div className="col-span-12 lg:col-span-6">
            <div className="font-mono-label text-[11px] text-[#0F1117] mb-6 flex items-center gap-3">
              <StatusLED />
              / Case study
            </div>
            <h1
              data-testid="case-study-headline"
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-normal font-extrabold text-[#0F1117] text-balance"
            >
              Incubation <span className="text-chrome">In Action.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex lg:items-end">
            <div>
            <p data-testid="case-study-introduction" className="text-base md:text-lg text-[#3D4049] leading-relaxed">
              How DCYB Ventures takes a company from the network to a
              hyperscale campus — proof the model builds real infrastructure.
            </p>
            <InlineChatLink testId="case-study-chat-button" />
            </div>
          </div>
        </div>

        {/* Main case card */}
        <div className="chrome-surface relative p-7 md:p-12 mb-6 md:mb-8">
          <CornerMarks color="#A9ADB4" />
          <div className="grid grid-cols-12 gap-y-10 gap-x-0 lg:gap-x-8">
            <div className="col-span-12 lg:col-span-7">
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4 mb-6">
                <img
                  src="/pvdc-logo.png"
                  alt="Pura Vida Data Centers"
                  data-testid="case-study-company-logo"
                  className="h-10 sm:h-12 max-w-full w-auto object-contain"
                />
                <span data-testid="case-study-portfolio-label" className="font-mono-label text-[9.5px] !tracking-normal uppercase text-[#5F6168] border hairline px-2.5 py-1">
                  DCYB Ventures · Portfolio
                </span>
              </div>
              <h3
                data-testid="case-study-title"
                className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0F1117] mb-5"
              >
                Pura Vida <span className="text-chrome">Data Centers.</span>
              </h3>
              <p data-testid="case-study-project-summary" className="text-[15px] md:text-base text-[#3D4049] leading-relaxed mb-4 max-w-xl">
                Co-developed by Luke Adams, Pura Vida Data Centers is a planned
                100-to-120-megawatt hyperscale campus in Guanacaste, Costa
                Rica — positioning Latin America as the next AI infrastructure
                hub on a renewable-led grid.
              </p>
              <p data-testid="case-study-workforce-summary" className="text-[15px] md:text-base text-[#3D4049] leading-relaxed mb-8 max-w-xl">
                Physical infrastructure and human infrastructure, together: the
                campus expands where AI gets built, while DCYB trains the
                workforce that builds and runs it.
              </p>
              <blockquote
                data-testid="case-study-quote"
                className="border-l-2 border-[#0F1117] pl-5 mb-8"
              >
                <p className="font-display text-lg md:text-xl font-extrabold tracking-tight leading-snug text-[#0F1117]">
                  “Data centers follow more than demand. They follow power,
                  connectivity, political stability, and long-term planning.
                  Costa Rica checks many of those boxes.”
                </p>
                <footer className="mt-3 font-mono-label text-[10px] tracking-[0.18em] uppercase text-[#71747B]">
                  — Luke Adams · Indie Hackers, Jul 2026
                </footer>
              </blockquote>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setApplyOpen(true)}
                  data-testid="case-study-apply-btn"
                  className="chrome-btn-dark chrome-btn-dark-glow inline-flex items-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-6 py-3.5"
                >
                  <span className="live-dot" aria-hidden />
                  Apply To DCYB Ventures
                  <ArrowUpRight size={14} />
                </button>
                <a
                  href="https://www.indiehackers.com/post/luke-adams-is-betting-costa-rica-will-become-latin-america-s-next-ai-infrastructure-hub-jlLsZOemz1Z9IWz9kh25"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="case-study-read-btn"
                  className="chrome-btn inline-flex items-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-6 py-3.5"
                >
                  Read The Coverage
                  <ArrowUpRight size={14} />
                </a>
                <button
                  type="button"
                  onClick={() => setDeckOpen(true)}
                  data-testid="case-study-deck-btn"
                  className="chrome-btn inline-flex items-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-6 py-3.5"
                >
                  Request The Ventures Deck
                  <ArrowUpRight size={14} />
                </button>
              </div>
            </div>

            {/* Stats */}
            <div
              data-testid="case-study-stats"
              className="col-span-12 lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 content-start"
            >
              {STATS.map(({ icon: Icon, v, k }, index) => (
                <div
                  key={k}
                  data-testid={`case-study-stat-${index}`}
                  className="min-w-0 bg-white border border-[#D2D5DA] p-4 md:p-6"
                >
                  <Icon size={15} className="text-[#5F6168] mb-4" aria-hidden />
                  <div data-testid={`case-study-stat-value-${index}`} className="font-display text-lg md:text-2xl font-extrabold tracking-normal text-[#0F1117] mb-1.5 break-words">
                    {v}
                  </div>
                  <div data-testid={`case-study-stat-label-${index}`} className="font-mono-label text-[9px] !tracking-normal uppercase text-[#71747B] leading-relaxed">
                    {k}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ventures model strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              data-testid={`ventures-model-step-${i}`}
              className="relative bg-[#0F1117] p-5 md:p-6 border border-[#1A1D26]"
            >
              <div className="font-mono-label text-[10px] tracking-[0.2em] text-[#8A8D94] mb-4">
                {s.n}
              </div>
              <div className="font-display text-lg font-extrabold tracking-tight text-white mb-1.5">
                {s.t}
              </div>
              <div className="text-[13px] text-[#A9ADB4] leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setApplyOpen(true)}
          data-testid="case-study-apply-footer-btn"
          className="font-mono-label text-[10px] tracking-[0.16em] uppercase text-[#0F1117] underline underline-offset-4 decoration-[#A9ADB4] hover:decoration-[#0F1117] transition-colors"
        >
          Build your company inside DCYB — apply now · every application receives a reply
        </button>
      </div>

      <VentureApplyModal open={applyOpen} onClose={() => setApplyOpen(false)} />
      {deckOpen && <DeckAccessModal open onClose={() => setDeckOpen(false)} deck={VENTURES_DECK} />}
    </section>
  );
}
